import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { blue, red } from '@mui/material/colors';
import {
  GridColDef,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid';

import Table from '~/components/v2/common/Table/Table';

import * as AccommodationService from '~/services/AccommodationService';
import { FacilityUpdatePayload } from '~/services/AccommodationService';

import ManageCatalogGroupsModal from './ManageCatalogGroupsModal';

const PAGE_SIZE = 50;

interface FacilityRowData extends AccommodationService.AdminFacility {
  isModified?: boolean;
  pendingChanges?: Partial<AccommodationService.AdminFacility>;
  isNew?: boolean;
}

const NEW_ROW_CODE_PREFIX = 'new-';

interface NewFacilityChanges extends FacilityUpdatePayload {
  code?: string;
}

interface CatalogGroupOption {
  id: string;
  name: string;
  code: string;
}

function EditableTextField(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    apiRef.current.setEditCellValue({ id, field, value: event.target.value });
  };

  return <TextField size="small" value={value ?? ''} onChange={handleChange} fullWidth variant="outlined" />;
}

function AliasesEditCell(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();
  const aliasesValue = Array.isArray(value) ? value.join(', ') : '';

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    apiRef.current.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <TextField
      size="small"
      value={aliasesValue}
      onChange={handleChange}
      fullWidth
      variant="outlined"
      placeholder="Comma-separated aliases"
    />
  );
}

function CatalogGroupEditCell(
  props: GridRenderEditCellParams & { catalogGroups: Array<AccommodationService.AdminCatalogGroup> },
) {
  const { id, field, value, catalogGroups } = props;
  const apiRef = useGridApiContext();

  const options: Array<CatalogGroupOption> = catalogGroups.map((group) => ({
    id: group.id,
    name: group.name,
    code: group.code,
  }));

  const selectedOption = options.find((opt) => opt.id === value?.id) ?? null;

  const handleChange = (_event: React.SyntheticEvent, newValue: CatalogGroupOption | null) => {
    if (newValue) {
      apiRef.current.setEditCellValue({
        id,
        field,
        value: { id: newValue.id, code: newValue.code, name: newValue.name },
      });
    }
  };

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => <TextField {...params} variant="outlined" />}
      fullWidth
      disableClearable
    />
  );
}

function FeaturedEditCell(props: GridRenderEditCellParams) {
  const { id, field, value } = props;
  const apiRef = useGridApiContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    apiRef.current.setEditCellValue({ id, field, value: event.target.checked });
  };

  return <Checkbox checked={Boolean(value)} onChange={handleChange} />;
}

export default function AccommodationFacilitiesResultsContainer() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const apiRef = useGridApiRef();
  const [page, setPage] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [filterCatalogGroupId, setFilterCatalogGroupId] = useState<string | undefined>(undefined);
  const [modifiedRows, setModifiedRows] = useState<Record<string, NewFacilityChanges>>({});
  const [isManageGroupsModalOpen, setIsManageGroupsModalOpen] = useState(false);
  const [deletingCodes, setDeletingCodes] = useState<Set<string>>(new Set());
  const [localRows, setLocalRows] = useState<Array<FacilityRowData>>([]);

  // Refs to avoid stale closures in callbacks used by DataGrid
  const modifiedRowsRef = useRef<Record<string, NewFacilityChanges>>({});
  const facilitiesRef = useRef<Array<AccommodationService.AdminFacility>>([]);

  // Keep refs in sync with state
  useEffect(() => {
    modifiedRowsRef.current = modifiedRows;
  }, [modifiedRows]);

  // Fetch catalog groups for dropdown
  const { data: catalogGroupsData } = useQuery({
    queryKey: ['accommodation', 'catalog-groups'],
    queryFn: async () => {
      const response = await AccommodationService.listCatalogGroups({ limit: '1000', page: '1' });
      return response.result;
    },
  });

  const catalogGroups = catalogGroupsData?.groups ?? [];

  // Fetch facilities
  const {
    data: facilitiesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['accommodation', 'facilities', { page: page + 1, searchName, filterCatalogGroupId }],
    queryFn: async () => {
      const response = await AccommodationService.listFacilities({
        name: searchName || undefined,
        catalogGroupId: filterCatalogGroupId,
        limit: String(PAGE_SIZE),
        page: String(page + 1),
      });
      return response.result;
    },
  });

  const facilities = facilitiesData?.facilities ?? [];
  const totalCount = facilitiesData?.total ?? 0;

  // Keep facilities ref in sync
  useEffect(() => {
    facilitiesRef.current = facilities;
  }, [facilities]);

  // Update facility mutation
  const updateFacilityMutation = useMutation({
    mutationFn: async ({ code, data }: { code: string; data: FacilityUpdatePayload }) => {
      return await AccommodationService.updateFacility({ code }, data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'facilities'] });
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.code];
        return updated;
      });
      enqueueSnackbar('Facility updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating facility: ${error.message}`, { variant: 'error' });
    },
  });

  // Delete facility mutation
  const deleteFacilityMutation = useMutation({
    mutationFn: async (code: string) => {
      return await AccommodationService.deleteFacility({ code });
    },
    onSuccess: (_data, code) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'facilities'] });
      setDeletingCodes((prev) => {
        const updated = new Set(prev);
        updated.delete(code);
        return updated;
      });
      enqueueSnackbar('Facility deleted successfully', { variant: 'success' });
    },
    onError: (error: Error, code) => {
      setDeletingCodes((prev) => {
        const updated = new Set(prev);
        updated.delete(code);
        return updated;
      });
      enqueueSnackbar(`Error deleting facility: ${error.message}`, { variant: 'error' });
    },
  });

  // Create facility mutation
  const createFacilityMutation = useMutation({
    mutationFn: async (data: AccommodationService.FacilityCreatePayload & { tempCode: string }) => {
      const { tempCode, ...payload } = data;
      return await AccommodationService.createFacility(payload);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['accommodation', 'facilities'] });
      setLocalRows((prev) => prev.filter((r) => r.code !== variables.tempCode));
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[variables.tempCode];
        return updated;
      });
      enqueueSnackbar('Facility created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating facility: ${error.message}`, { variant: 'error' });
    },
  });

  const handleAddNew = useCallback(() => {
    const tempCode = `${NEW_ROW_CODE_PREFIX}${Date.now()}`;
    const newRow: FacilityRowData = {
      code: tempCode,
      name: '',
      description: null,
      aliases: [],
      catalogGroup: undefined,
      icon: null,
      featured: false,
      displayOrder: undefined,
      isNew: true,
    };
    setLocalRows((prev) => [newRow, ...prev]);
    setModifiedRows((prev) => ({
      ...prev,
      [tempCode]: { code: '' },
    }));
  }, []);

  const handleSave = useCallback(
    (facility: FacilityRowData) => {
      // Use ref to get current changes to avoid stale closure
      const changes = modifiedRowsRef.current[facility.code];
      if (!changes) return;

      if (facility.isNew || facility.code.startsWith(NEW_ROW_CODE_PREFIX)) {
        // Create new facility
        if (!changes.code || !changes.catalogGroupId) {
          enqueueSnackbar('Code and Group are required for new facilities', { variant: 'error' });
          return;
        }
        createFacilityMutation.mutate({
          tempCode: facility.code,
          code: changes.code,
          name: changes.name ?? '',
          description: changes.description,
          aliases: changes.aliases,
          catalogGroupId: changes.catalogGroupId,
          displayOrder: changes.displayOrder,
          featured: changes.featured,
        });
      } else {
        // Update existing facility
        updateFacilityMutation.mutate({
          code: facility.code,
          data: changes,
        });
      }
    },
    [updateFacilityMutation, createFacilityMutation, enqueueSnackbar],
  );

  const handleClearChanges = useCallback(
    (code: string) => {
      const isNewRow = code.startsWith(NEW_ROW_CODE_PREFIX);

      if (isNewRow) {
        // For new rows, remove them entirely
        setLocalRows((prev) => prev.filter((r) => r.code !== code));
      } else {
        // Find the original facility data - use ref to avoid stale closure
        const originalFacility = facilitiesRef.current.find((f) => f.code === code);
        if (originalFacility && apiRef.current) {
          // Stop any active cell editing first (ignore errors for cells not in edit mode)
          const editableFields = ['name', 'description', 'aliases', 'catalogGroup', 'displayOrder', 'featured'];
          editableFields.forEach((field) => {
            try {
              apiRef.current.stopCellEditMode({ id: code, field, ignoreModifications: true });
            } catch {
              // Cell not in edit mode, ignore
            }
          });
          // Restore the row to its original values in the grid
          apiRef.current.updateRows([{ ...originalFacility }]);
        }
      }
      // Clear the tracked changes
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[code];
        return updated;
      });
    },
    [apiRef],
  );

  const handleDelete = useCallback(
    (code: string) => {
      const isNewRow = code.startsWith(NEW_ROW_CODE_PREFIX);

      if (isNewRow) {
        // For new rows, just remove locally
        setLocalRows((prev) => prev.filter((r) => r.code !== code));
        setModifiedRows((prev) => {
          const updated = { ...prev };
          delete updated[code];
          return updated;
        });
      } else {
        // Delete from API
        setDeletingCodes((prev) => new Set(prev).add(code));
        deleteFacilityMutation.mutate(code);
      }
    },
    [deleteFacilityMutation],
  );

  const processRowUpdate = useCallback((newRow: FacilityRowData, _oldRow: FacilityRowData) => {
    const isNewRow = newRow.code.startsWith(NEW_ROW_CODE_PREFIX) || newRow.isNew;

    if (isNewRow) {
      // For new rows, track all fields - use ref to avoid stale closure
      const currentModifiedRows = modifiedRowsRef.current;
      const changedFields: NewFacilityChanges = {
        code: (newRow as any).newCode || currentModifiedRows[newRow.code]?.code || '',
        name: newRow.name,
        description: newRow.description,
        aliases: newRow.aliases,
        catalogGroupId: newRow.catalogGroup?.id,
        displayOrder: newRow.displayOrder,
        featured: newRow.featured,
      };
      setModifiedRows((prev) => ({
        ...prev,
        [newRow.code]: { ...prev[newRow.code], ...changedFields },
      }));
      return newRow;
    }

    // Find the original facility data from API to compare against - use ref
    const originalFacility = facilitiesRef.current.find((f) => f.code === newRow.code);
    if (!originalFacility) return newRow;

    const changedFields: NewFacilityChanges = {};

    if (newRow.name !== originalFacility.name) changedFields.name = newRow.name;
    if (newRow.description !== originalFacility.description) changedFields.description = newRow.description;
    if (JSON.stringify(newRow.aliases) !== JSON.stringify(originalFacility.aliases))
      changedFields.aliases = newRow.aliases;
    if (newRow.displayOrder !== originalFacility.displayOrder) changedFields.displayOrder = newRow.displayOrder;
    if (newRow.featured !== originalFacility.featured) changedFields.featured = newRow.featured;
    if (newRow.catalogGroup?.id !== originalFacility.catalogGroup?.id)
      changedFields.catalogGroupId = newRow.catalogGroup?.id;

    if (Object.keys(changedFields).length > 0) {
      setModifiedRows((prev) => ({
        ...prev,
        [newRow.code]: changedFields,
      }));
    } else {
      // If no changes compared to original, remove from modifiedRows
      setModifiedRows((prev) => {
        const updated = { ...prev };
        delete updated[newRow.code];
        return updated;
      });
    }

    return newRow;
  }, []);

  const handleSearchSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(0);
  }, []);

  const columns: Array<GridColDef<FacilityRowData>> = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        width: 180,
        editable: true,
        renderEditCell: (params) => {
          const isNewRow = String(params.id).startsWith(NEW_ROW_CODE_PREFIX);
          if (!isNewRow) {
            return <Typography variant="body2">{params.value}</Typography>;
          }
          // For new rows, we need a special handler to update the code in modifiedRows
          const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newCode = event.target.value;
            setModifiedRows((prev) => ({
              ...prev,
              [params.id]: { ...prev[params.id], code: newCode },
            }));
          };
          // Use ref to get current value to avoid stale closure
          return (
            <TextField
              size="small"
              value={modifiedRowsRef.current[params.id]?.code ?? ''}
              onChange={handleCodeChange}
              fullWidth
              variant="outlined"
              placeholder="Enter facility code"
            />
          );
        },
        renderCell: (params: GridRenderCellParams<FacilityRowData>) => {
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);
          if (isNewRow) {
            // Use ref to get current value to avoid stale closure
            return (
              <Typography variant="body2" color="textSecondary">
                {modifiedRowsRef.current[params.row.code]?.code || '(enter code)'}
              </Typography>
            );
          }
          return <Typography variant="body2">{params.value}</Typography>;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        editable: true,
        renderEditCell: (params) => <EditableTextField {...params} />,
      },
      {
        field: 'description',
        headerName: 'Description',
        minWidth: 250,
        editable: true,
        renderEditCell: (params) => <EditableTextField {...params} />,
        renderCell: (params: GridRenderCellParams<FacilityRowData>) => (
          <Typography variant="body2" noWrap title={params.value ?? ''}>
            {params.value ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'aliases',
        headerName: 'Aliases',
        width: 200,
        editable: true,
        renderEditCell: (params) => <AliasesEditCell {...params} />,
        renderCell: (params: GridRenderCellParams<FacilityRowData>) => (
          <Typography variant="body2" noWrap title={params.value?.join(', ') ?? ''}>
            {params.value?.join(', ') ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'catalogGroup',
        headerName: 'Group',
        width: 180,
        editable: true,
        renderEditCell: (params) => <CatalogGroupEditCell {...params} catalogGroups={catalogGroups} />,
        renderCell: (params: GridRenderCellParams<FacilityRowData>) => (
          <Typography variant="body2">{params.value?.name ?? '-'}</Typography>
        ),
        valueGetter: (_value, row) => row.catalogGroup,
      },
      {
        field: 'displayOrder',
        headerName: 'Display Order',
        width: 80,
        editable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'featured',
        headerName: 'Featured',
        width: 80,
        editable: true,
        renderEditCell: (params) => <FeaturedEditCell {...params} />,
        renderCell: (params: GridRenderCellParams<FacilityRowData>) => (
          <Checkbox checked={Boolean(params.value)} disabled />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        editable: false,
        renderCell: (params: GridRenderCellParams<FacilityRowData>) => {
          // Use ref to check for changes to avoid stale closure
          const hasChanges = Boolean(modifiedRowsRef.current[params.row.code]);
          const isSaving = updateFacilityMutation.isPending || createFacilityMutation.isPending;
          const isDeleting = deletingCodes.has(params.row.code);
          const isNewRow = params.row.code.startsWith(NEW_ROW_CODE_PREFIX);

          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={isNewRow ? 'Create facility' : 'Save changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleSave(params.row)}
                    disabled={!hasChanges || isSaving || isDeleting}
                    sx={{ color: hasChanges && !isSaving ? blue[600] : undefined }}
                  >
                    {isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Clear changes'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleClearChanges(params.row.code)}
                    disabled={!hasChanges || isSaving || isDeleting}
                  >
                    <ClearIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={isNewRow ? 'Remove row' : 'Delete facility'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(params.row.code)}
                    disabled={isSaving || isDeleting}
                    sx={{ color: red[600] }}
                  >
                    {isDeleting ? <CircularProgress size={18} /> : <DeleteIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [
      catalogGroups,
      handleSave,
      handleClearChanges,
      handleDelete,
      updateFacilityMutation.isPending,
      createFacilityMutation.isPending,
      deletingCodes,
    ],
  );

  // Combine local new rows with API rows (local rows first to appear at top)
  const allRows = useMemo(() => {
    return [...localRows, ...facilities];
  }, [localRows, facilities]);

  const catalogGroupOptions: Array<CatalogGroupOption & { id: string }> = catalogGroups.map((group) => ({
    id: group.id,
    name: group.name,
    code: group.code,
  }));

  const selectedFilterGroup = catalogGroupOptions.find((opt) => opt.id === filterCatalogGroupId) ?? null;

  if (error) {
    return (
      <Alert severity="error">
        Error loading facilities: {error instanceof Error ? error.message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Stack direction="column" spacing={2} sx={{ pt: 2 }}>
      <Box component="form" onSubmit={handleSearchSubmit}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            label="Search by name"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <Autocomplete
            size="small"
            options={catalogGroupOptions}
            value={selectedFilterGroup}
            onChange={(_event, newValue) => {
              setFilterCatalogGroupId(newValue?.id);
              setPage(0);
            }}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Filter by Catalog Group" variant="outlined" />}
            sx={{ minWidth: 250 }}
          />
          <Button variant="contained" type="submit">
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchName('');
              setFilterCatalogGroupId(undefined);
              setPage(0);
            }}
          >
            Clear
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
            Add New
          </Button>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setIsManageGroupsModalOpen(true)}>
            Manage Groups
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="textSecondary">
        Click on a cell to edit. Changes are tracked per row - click Save to persist changes for that facility.
      </Typography>

      <Table
        columns={columns}
        rows={allRows}
        getRowId={(row) => row.code}
        paginationMode="server"
        paginationModel={{ page, pageSize: PAGE_SIZE }}
        onPaginationModelChange={(model) => setPage(model.page)}
        totalRowCount={totalCount + localRows.length}
        isLoading={isLoading}
        isDisabled
        autoHeight
        dataGridProps={{
          apiRef,
          editMode: 'cell',
          processRowUpdate,
          onProcessRowUpdateError: (error) => {
            enqueueSnackbar(`Error processing row update: ${error.message}`, { variant: 'error' });
          },
          getRowHeight: () => 'auto',
          sx: {
            '& .MuiDataGrid-cell': {
              py: 1,
            },
          },
        }}
      />

      <ManageCatalogGroupsModal open={isManageGroupsModalOpen} onClose={() => setIsManageGroupsModalOpen(false)} />
    </Stack>
  );
}
